import { TIO_API_BASE_URL } from '../utils/constants';
import apiServiceInstance from './apiService';
import { MARSHALL_PALMER_TIO_GRADIENT } from '../pages/Nowcasting/nowcastingConstants';

/**
 * Tomorrow.io Map Tiles Service
 * 
 * Provides direct access to Tomorrow.io's Map Tiles API.
 * Uses user authentication tokens for API access.
 * 
 * Docs: https://docs.tomorrow.io/reference/get-map-tile
 */

// Available data layers for Tomorrow.io map tiles
export const TIO_TILE_FIELDS = {
  // Precipitation
  precipitationIntensity: { label: 'Precipitation Intensity', category: 'precipitation' },
  rainIntensity: { label: 'Rain Intensity', category: 'precipitation' },
  snowIntensity: { label: 'Snow Intensity', category: 'precipitation' },
  sleetIntensity: { label: 'Sleet Intensity', category: 'precipitation' },
  freezingRainIntensity: { label: 'Freezing Rain', category: 'precipitation' },
  snowAccumulation: { label: 'Snow Accumulation', category: 'precipitation' },
  iceAccumulation: { label: 'Ice Accumulation', category: 'precipitation' },
  
  // Temperature & Humidity
  temperature: { label: 'Temperature', category: 'temperature' },
  temperatureApparent: { label: 'Feels Like', category: 'temperature' },
  dewPoint: { label: 'Dew Point', category: 'temperature' },
  humidity: { label: 'Humidity', category: 'humidity' },
  
  // Wind
  windSpeed: { label: 'Wind Speed', category: 'wind' },
  windGust: { label: 'Wind Gust', category: 'wind' },
  windDirection: { label: 'Wind Direction', category: 'wind' },
  
  // Pressure & Visibility
  pressureSurfaceLevel: { label: 'Surface Pressure', category: 'pressure' },
  pressureSeaLevel: { label: 'Sea Level Pressure', category: 'pressure' },
  visibility: { label: 'Visibility', category: 'visibility' },
  
  // Clouds
  cloudCover: { label: 'Cloud Cover', category: 'clouds' },
  cloudBase: { label: 'Cloud Base', category: 'clouds' },
  cloudCeiling: { label: 'Cloud Ceiling', category: 'clouds' },
  
  // UV & Air Quality
  uvIndex: { label: 'UV Index', category: 'uv' },
  epaIndex: { label: 'EPA Index', category: 'air_quality' },
  particulateMatter25: { label: 'PM2.5', category: 'air_quality' },
  particulateMatter10: { label: 'PM10', category: 'air_quality' },
  
  // Pollen
  grassIndex: { label: 'Grass Pollen', category: 'pollen' },
  treeIndex: { label: 'Tree Pollen', category: 'pollen' },
  weedIndex: { label: 'Weed Pollen', category: 'pollen' },
  
  // Fire & Soil
  fireIndex: { label: 'Fire Index', category: 'fire' },
  soilMoistureVolumetric0To10: { label: 'Soil Moisture (0-10cm)', category: 'soil' },
  soilTemperature0To10: { label: 'Soil Temperature (0-10cm)', category: 'soil' },
} as const;

export type TioTileField = keyof typeof TIO_TILE_FIELDS;

export interface TioAvailableTimes {
  minDate: string;
  maxDate: string;
  current: string;
  description: string;
  fields: string[];
  zoomRange: { min: number; max: number };
}

/**
 * Generates the tile URL template for Mapbox/Leaflet to use
 * @param field The data layer to display
 * @param time ISO 8601 timestamp or relative time (e.g., 'now', '+1h')
 * @param format Image format ('png' or 'pbf')
 * @returns URL template with {z}, {x}, {y} placeholders and token query parameter
 */
export function getTioTileUrlTemplate(
  field: TioTileField = 'precipitationIntensity',
  time: string = 'now',
  format: 'png' | 'pbf' = 'png',
  excludedLayers?: string
): string {
  // Get the user token for authentication (Mapbox can't send headers for tile requests)
  const token = apiServiceInstance.getToken();
  
  // Ensure time is in ISO 8601 format (e.g., 2024-01-15T12:00:00Z)
  // For relative times like 'now', '+1h', etc., use as-is
  // For Date objects or other formats, convert to ISO 8601
  let isoTime: string;
  if (time === 'now' || time.startsWith('+') || time.startsWith('-')) {
    // Relative time - use as-is
    isoTime = time;
  } else {
    // Try to parse and format as ISO 8601
    try {
      const date = new Date(time);
      if (!isNaN(date.getTime())) {
        // Remove milliseconds for cleaner ISO format (Tomorrow.io prefers this)
        isoTime = date.toISOString().replace(/\.\d{3}Z$/, 'Z');
      } else {
        // If parsing fails, assume it's already in correct format
        isoTime = time;
      }
    } catch {
      // If parsing fails, use as-is
      isoTime = time;
    }
  }
  
  // Build query string
  const params: string[] = [];
  if (token) params.push(`jwt=${encodeURIComponent(token)}`);
  if (excludedLayers) params.push(`excludedLayers=${encodeURIComponent(excludedLayers)}`);

  const precipFields: TioTileField[] = ['precipitationIntensity', 'rainIntensity'];
  if (precipFields.includes(field)) {
    params.push(`gradient=${encodeURIComponent(MARSHALL_PALMER_TIO_GRADIENT)}`);
  }

  const query = params.length > 0 ? `?${params.join('&')}` : '';

  return `${TIO_API_BASE_URL}/map/tile/{z}/{x}/{y}/${field}/${isoTime}.${format}${query}`;
}

export interface GenerateTioTimestampsOptions {
  /** Interval for high-resolution period (minutes). Default: 10 */
  highResIntervalMinutes?: number;
  /** Interval for low-resolution period (minutes). Default: 60 */
  lowResIntervalMinutes?: number;
  /** Duration of high-resolution period from current time (hours). Default: 2 */
  highResHours?: number;
  /** Whether to use variable intervals based on proximity to current time. Default: false for backward compatibility */
  useVariableIntervals?: boolean;
}

/**
 * Generates a list of timestamps for animation playback
 * @param startTime Start of the time range
 * @param endTime End of the time range
 * @param intervalMinutesOrOptions Either a fixed interval in minutes, or options for variable intervals
 * @returns Array of ISO 8601 timestamps
 * 
 * When useVariableIntervals is true:
 * - Timestamps within highResHours (default 2h) of current time use highResIntervalMinutes (default 10min)
 * - Timestamps beyond that threshold use lowResIntervalMinutes (default 60min)
 * 
 * This optimization is based on Tomorrow.io's model behavior:
 * - Near-term nowcast data updates on a minutely basis
 * - Forecast data beyond ~2 hours updates hourly
 */
export function generateTioTimestamps(
  startTime: Date,
  endTime: Date,
  intervalMinutesOrOptions: number | GenerateTioTimestampsOptions = 60
): string[] {
  const timestamps: string[] = [];
  let current = new Date(startTime);
  
  // Keep the exact start time, just clear seconds/milliseconds for cleaner timestamps
  current.setSeconds(0, 0);

  // Handle backward compatibility with fixed interval
  if (typeof intervalMinutesOrOptions === 'number') {
    while (current <= endTime) {
      const isoString = current.toISOString().replace(/\.\d{3}Z$/, 'Z');
      timestamps.push(isoString);
      current = new Date(current.getTime() + intervalMinutesOrOptions * 60 * 1000);
    }
    return timestamps;
  }

  // Variable interval mode
  const options = intervalMinutesOrOptions;
  const highResInterval = options.highResIntervalMinutes ?? 10;
  const lowResInterval = options.lowResIntervalMinutes ?? 60;
  const highResHours = options.highResHours ?? 2;
  
  if (!options.useVariableIntervals) {
    // Default to high-res interval if variable intervals not enabled
    while (current <= endTime) {
      const isoString = current.toISOString().replace(/\.\d{3}Z$/, 'Z');
      timestamps.push(isoString);
      current = new Date(current.getTime() + highResInterval * 60 * 1000);
    }
    return timestamps;
  }

  // Calculate the threshold: current time + highResHours
  const now = new Date();
  const highResThreshold = new Date(now.getTime() + highResHours * 60 * 60 * 1000);
  
  while (current <= endTime) {
    const isoString = current.toISOString().replace(/\.\d{3}Z$/, 'Z');
    timestamps.push(isoString);
    
    // Determine interval based on whether current timestamp is within high-res period
    const intervalToUse = current < highResThreshold ? highResInterval : lowResInterval;
    current = new Date(current.getTime() + intervalToUse * 60 * 1000);
  }
  
  return timestamps;
}

/**
 * Gets the available time range for Tomorrow.io map tiles
 * @returns Object with min/max dates and available fields
 */
export async function getTioAvailableTimes(): Promise<TioAvailableTimes> {
  // Return hardcoded values based on Tomorrow.io API documentation
  // Map tiles are available from -7 days to +15 days from current time
  const now = new Date();
  const minDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000); // -7 days
  const maxDate = new Date(now.getTime() + 15 * 24 * 60 * 60 * 1000); // +15 days
  
  return {
    minDate: minDate.toISOString(),
    maxDate: maxDate.toISOString(),
    current: now.toISOString(),
    description: 'Tomorrow.io map tiles are available from -7 days to +15 days from the current time',
    fields: Object.keys(TIO_TILE_FIELDS),
    zoomRange: { min: 1, max: 12 },
  };
}

/**
 * Converts latitude/longitude to tile coordinates
 * @param lat Latitude
 * @param lon Longitude
 * @param zoom Zoom level
 * @returns Object with x and y tile coordinates
 */
export function latLonToTile(lat: number, lon: number, zoom: number): { x: number; y: number } {
  const n = Math.pow(2, zoom);
  const x = Math.floor(((lon + 180) / 360) * n);
  const latRad = (lat * Math.PI) / 180;
  const y = Math.floor(((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n);
  return { x, y };
}

/**
 * Converts tile coordinates to latitude/longitude bounds
 * @param x X tile coordinate
 * @param y Y tile coordinate
 * @param zoom Zoom level
 * @returns Object with northwest and southeast corners
 */
export function tileToLatLonBounds(
  x: number,
  y: number,
  zoom: number
): { nw: { lat: number; lon: number }; se: { lat: number; lon: number } } {
  const n = Math.pow(2, zoom);
  
  const lonNw = (x / n) * 360 - 180;
  const latRadNw = Math.atan(Math.sinh(Math.PI * (1 - (2 * y) / n)));
  const latNw = (latRadNw * 180) / Math.PI;
  
  const lonSe = ((x + 1) / n) * 360 - 180;
  const latRadSe = Math.atan(Math.sinh(Math.PI * (1 - (2 * (y + 1)) / n)));
  const latSe = (latRadSe * 180) / Math.PI;
  
  return {
    nw: { lat: latNw, lon: lonNw },
    se: { lat: latSe, lon: lonSe },
  };
}

const tomorrowIoTilesService = {
  getTileUrlTemplate: getTioTileUrlTemplate,
  generateTimestamps: generateTioTimestamps,
  getAvailableTimes: getTioAvailableTimes,
  latLonToTile,
  tileToLatLonBounds,
  fields: TIO_TILE_FIELDS,
};

export default tomorrowIoTilesService;
